/**
 * DesignMapScreen — full-screen map workspace for a generated design (the v3
 * MapScreen). Left: read-only brief summary. Centre: the design on the real map
 * (3D) or plan (2D). Right: the design inspector in map mode, with Remove +
 * Keep·next / Finalize. Top bar steps through designs across revisions.
 */
import { useEffect, useRef, useState } from "react";
import { observer } from "mobx-react-lite";
import type { Editor } from "src/types/Editor";
import type { World } from "src/types/World";
import { Selection } from "src/types/Selection";
import { MapState } from "src/types/MapState";
import { Icon, ACCENT } from "./Icon";
import { Plan2D } from "../Plan2D";
import { DesignMap } from "./DesignMap";
import { DesignInspector } from "./DesignInspector";
import { OverlayControls } from "./OverlayControls";
import { deriveRows } from "./ExploreView";
import type { BoardState } from "./BoardState";
import { UNIT_MIX, type BriefSectionState } from "./briefModel";

const icons = {
  back: <path d="M15 6l-6 6 6 6" />,
  chevL: <path d="M15 6l-6 6 6 6" />,
  chevR: <path d="M9 6l6 6-6 6" />,
  chevD: <path d="M6 9l6 6 6-6" />,
  folder: <path d="M3 6a1 1 0 0 1 1-1h5l2 2h8a1 1 0 0 1 1 1v9a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1z" />,
  x: <path d="M6 6l12 12M18 6L6 18" />,
  layers: <path d="M12 3l9 5-9 5-9-5 9-5zM3 13l9 5 9-5" />,
  edit: <path d="M4 20h4l10-10-4-4L4 16v4zM14 6l4 4" />,
  pin: (
    <g>
      <path d="M12 21s7-6.4 7-11a7 7 0 1 0-14 0c0 4.6 7 11 7 11z" />
      <circle cx="12" cy="10" r="2.5" />
    </g>
  ),
};

export interface MapDesignRef {
  sectionId: string;
  key: string;
}

export const DesignMapScreen = observer(function DesignMapScreen({
  editor,
  board,
  sections,
  active,
  setActive,
  mode3d,
  setMode3d,
  onRemove,
  onFinalize,
  canFinalize,
  onClose,
  onEditBrief,
  onRegenerateSection,
}: {
  editor: Editor;
  board: BoardState;
  sections: BriefSectionState[];
  active: MapDesignRef;
  setActive: (ref: MapDesignRef) => void;
  mode3d: boolean;
  setMode3d: (v: boolean) => void;
  onRemove: (sectionId: string, key: string) => void;
  onFinalize: () => void;
  canFinalize: boolean;
  onClose: () => void;
  onEditBrief: (section: BriefSectionState) => void;
  onRegenerateSection: (sectionId: string, floorOverrides: Record<string, number>) => void;
}) {
  const flat = sections.flatMap((s) =>
    deriveRows(board.shownOf(s.id)).map((r) => ({ ...r, sectionId: s.id, section: s }))
  );
  const idx = flat.findIndex((f) => f.sectionId === active.sectionId && f.key === active.key);
  const current = idx >= 0 ? flat[idx] : null;

  const activeItem = current?.item ?? null;
  const [briefOpen, setBriefOpen] = useState(true);
  const [ovOpen, setOvOpen] = useState(false);
  const [world, setWorld] = useState<World | null>(null);
  const [mapState, setMapState] = useState<MapState | null>(null);
  const mapStateRef = useRef<MapState | null>(null);
  const [selection] = useState(() => new Selection(null));

  // Step the active design onto a SINGLE persistent map state (never touches
  // editor.world / editor.mapState). The first design builds the map state;
  // later steps just point it at the new world — like the main canvas — so only
  // the building geometry swaps and the basemap is never torn down and reloaded.
  // The previous design stays on screen until the next one is ready (no blank).
  useEffect(() => {
    let cancelled = false;
    const base = editor.world;
    if (!activeItem || !base) return;
    editor.loader
      .loadFromDesignGenerator(activeItem, base)
      .then((dw) => {
        if (cancelled) return;
        selection.setWorld(dw);
        if (mapStateRef.current) {
          mapStateRef.current.setWorld(dw);
        } else {
          mapStateRef.current = new MapState(dw);
          setMapState(mapStateRef.current);
        }
        setWorld(dw);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [editor, activeItem, selection]);

  // If the design vanished (removed and was the last), close.
  useEffect(() => {
    if (flat.length === 0) onClose();
  }, [flat.length, onClose]);

  if (!current) {
    return (
      <div className="fixed inset-0 z-40 grid place-items-center bg-gray-100 text-[13px] text-[#9ca3af]">
        Loading…
      </div>
    );
  }

  const go = (dir: number) => {
    if (flat.length === 0) return;
    const ni = (idx + dir + flat.length) % flat.length;
    const f = flat[ni];
    setActive({ sectionId: f.sectionId, key: f.key });
  };
  const remove = () => {
    const next = flat[idx + 1] ?? flat[idx - 1] ?? null;
    onRemove(current.sectionId, current.key);
    if (next && next.key !== current.key) setActive({ sectionId: next.sectionId, key: next.key });
    else onClose();
  };
  const keepNext = () => go(1);

  return (
    <div className="fixed inset-0 z-40 flex flex-col bg-[#f3f4f6]">
      {/* top bar */}
      <div className="h-14 shrink-0 border-b border-[#d1d5db] bg-white flex items-center px-3 gap-3 relative">
        <button
          onClick={onClose}
          className="inline-flex items-center gap-1 h-8 px-2 rounded-md text-[12px] text-[#4b5563] hover:bg-[#f3f4f6]"
        >
          <Icon d={icons.back} size={16} /> Back
        </button>
        <div className="h-9 inline-flex items-center gap-2 pl-3 border-l border-[#e5e7eb]">
          <span style={{ color: ACCENT }}>
            <Icon d={icons.folder} size={14} />
          </span>
          <span className="text-[12px] text-[#9ca3af]">Designs</span>
          <Icon d={icons.chevR} size={12} className="text-[#d1d5db]" />
          <span className="text-[12px] font-medium text-[#4b5563]">{current.section.label}</span>
        </div>

        {/* design jumper */}
        <div className="absolute left-1/2 -translate-x-1/2 flex items-center gap-2">
          <button
            onClick={() => go(-1)}
            title="Previous design"
            className="w-9 h-9 grid place-items-center rounded-full border border-[#d1d5db] text-[#6b7280] hover:bg-[#f9fafb]"
          >
            <Icon d={icons.chevL} size={18} />
          </button>
          <div className="h-9 inline-flex items-center gap-1.5 px-3.5 rounded-full border border-[#d1d5db] bg-white min-w-[150px] justify-center">
            <span className="text-[14px] font-semibold text-[#111827] leading-none">
              {current.name}
            </span>
            <span className="ml-0.5 text-[11px] text-[#9ca3af] tabular-nums">
              {idx + 1}/{flat.length}
            </span>
          </div>
          <button
            onClick={() => go(1)}
            title="Next design"
            className="w-9 h-9 grid place-items-center rounded-full border border-[#d1d5db] text-[#6b7280] hover:bg-[#f9fafb]"
          >
            <Icon d={icons.chevR} size={18} />
          </button>
        </div>

        <div className="grow" />
        <Seg
          value={mode3d ? "3d" : "2d"}
          onChange={(v) => setMode3d(v === "3d")}
          options={[
            { value: "3d", label: "3D" },
            { value: "2d", label: "2D" },
          ]}
        />
      </div>

      <div className="grow min-h-0 flex">
        {/* left: read-only brief summary */}
        {briefOpen ? (
          <BriefSummary
            section={current.section}
            onCollapse={() => setBriefOpen(false)}
            onEditBrief={() => onEditBrief(current.section)}
          />
        ) : (
          <button
            onClick={() => setBriefOpen(true)}
            title="Show brief"
            className="w-9 shrink-0 border-r border-[#e5e7eb] bg-white flex flex-col items-center pt-3 gap-2 text-[#9ca3af] hover:text-[#374151]"
          >
            <Icon d={icons.chevR} size={16} />
            <span className="text-[10px] tracking-wide" style={{ writingMode: "vertical-rl" }}>
              Brief
            </span>
          </button>
        )}

        {/* centre: the design map / plan */}
        <div className="grow min-w-0 p-3">
          <div className="w-full h-full flex flex-col rounded-xl border border-[#e5e7eb] bg-white shadow-sm overflow-hidden">
            <div className="h-9 shrink-0 flex items-center gap-2 px-3 border-b border-[#e5e7eb]">
              <span className="w-2 h-2 rounded-full" style={{ background: ACCENT }} />
              <span className="text-[11px] font-medium text-[#4b5563]">Site map</span>
              <span className="text-[10px] text-[#9ca3af]">
                {mode3d ? "3D" : "2D"} · {current.name}
              </span>
              <div className="grow" />
              {mode3d && mapState && (
                <div className="relative">
                  <button
                    onClick={() => setOvOpen((v) => !v)}
                    className={
                      "inline-flex items-center gap-1.5 h-7 px-2.5 rounded-md text-[11px] font-medium border transition-colors " +
                      (mapState.stencil.loadedCatalogId
                        ? "text-[#7c5cff] border-[#c9bcff] bg-[rgba(124,92,255,0.06)]"
                        : "text-[#6b7280] border-[#e5e7eb] hover:bg-[#f9fafb]")
                    }
                  >
                    <Icon d={icons.layers} size={13} /> Overlays
                  </button>
                  {ovOpen && (
                    <>
                      <div className="fixed inset-0 z-10" onClick={() => setOvOpen(false)} />
                      <div className="absolute right-0 top-9 z-20 w-[280px] rounded-lg border border-[#e5e7eb] bg-white shadow-lg p-2">
                        <OverlayControls mapState={mapState} world={world} />
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
            <div className="grow relative overflow-hidden bg-[#f9fafb]">
              {!world || !mapState ? (
                <div className="absolute inset-0 grid place-items-center text-[12px] text-[#9ca3af]">
                  Loading site…
                </div>
              ) : mode3d ? (
                <DesignMap world={world} mapState={mapState} selection={selection} />
              ) : (
                <Plan2D world={world} />
              )}
            </div>
          </div>
        </div>

        {/* right: inspector in map mode */}
        <DesignInspector
          editor={editor}
          item={current.item}
          name={current.name}
          mode3d={mode3d}
          inMap
          canFinalize={canFinalize}
          bookmarked={board.bookmarks.has(current.key)}
          locked={board.locked.has(current.key)}
          onClose={onClose}
          onToggleBookmark={() => board.toggleBookmark(current.key)}
          onToggleLock={() => board.toggleLock(current.key)}
          onRemove={remove}
          onKeep={keepNext}
          onFinalize={onFinalize}
          onMap={() => {}}
          onRegenerate={(overrides) => {
            onRegenerateSection(current.sectionId, overrides);
            onClose();
          }}
        />
      </div>
    </div>
  );
});

function BriefSummary({
  section,
  onCollapse,
  onEditBrief,
}: {
  section: BriefSectionState;
  onCollapse: () => void;
  onEditBrief: () => void;
}) {
  const b = section.brief;
  const mix = b.mix.map((m, i) => ({ ...m, cat: UNIT_MIX[i] })).filter((m) => m.on);
  const rows: [string, string][] = [
    ["Floors", `${b.floors}`],
    ["Floor height", `${b.floorHeight} m`],
    ["Separation", `${b.separation} m`],
    ["Height range", `${b.minHeight}–${b.maxHeight} m`],
    ["Podium", b.podium.on ? `${b.podium.floors} fl` : "None"],
  ];
  return (
    <div className="w-[272px] shrink-0 border-r border-[#e5e7eb] bg-white flex flex-col">
      <div className="h-9 shrink-0 flex items-center gap-2 px-3 border-b border-[#e5e7eb]">
        <span className="text-[12px] font-semibold text-[#1f2937]">{section.label}</span>
        <span className="text-[10px] text-[#9ca3af]">· Brief</span>
        <button
          onClick={onCollapse}
          title="Collapse"
          className="ml-auto w-6 h-6 grid place-items-center rounded-sm text-[#9ca3af] hover:bg-[#f3f4f6]"
        >
          <Icon d={icons.chevL} size={14} />
        </button>
      </div>

      <div className="grow overflow-y-auto p-3 space-y-4">
        {section.address && (
          <div className="flex items-start gap-2 rounded-lg bg-[#f9fafb] border border-[#eef0f2] px-2.5 py-2">
            <span className="mt-px shrink-0" style={{ color: ACCENT }}>
              <Icon d={icons.pin} size={14} />
            </span>
            <span className="text-[11px] leading-snug text-[#4b5563]">{section.address}</span>
          </div>
        )}

        <section>
          <h4 className="text-[10px] font-semibold uppercase tracking-wide text-[#9ca3af] mb-1.5">
            Massing
          </h4>
          <dl className="rounded-lg border border-[#eef0f2] overflow-hidden">
            {rows.map(([k, v], i) => (
              <div
                key={k}
                className={
                  "flex items-center justify-between px-2.5 py-1.5 text-[11px] " +
                  (i % 2 ? "bg-white" : "bg-[#fafafa]")
                }
              >
                <dt className="text-[#9ca3af]">{k}</dt>
                <dd className="text-[12px] tabular-nums font-medium text-[#374151]">{v}</dd>
              </div>
            ))}
          </dl>
        </section>

        {mix.length > 0 && (
          <section>
            <h4 className="text-[10px] font-semibold uppercase tracking-wide text-[#9ca3af] mb-1.5">
              Unit mix
            </h4>
            <div className="space-y-1.5">
              {mix.map((m, i) => (
                <div key={i}>
                  <div className="flex items-center justify-between text-[11px] mb-0.5">
                    <span className="inline-flex items-center gap-1.5 text-[#4b5563]">
                      <span
                        className="w-2 h-2 rounded-full"
                        style={{ background: m.cat?.color ?? ACCENT }}
                      />
                      {m.cat?.label ?? `Type ${i + 1}`}
                    </span>
                    <span className="tabular-nums text-[#6b7280]">{m.pct}%</span>
                  </div>
                  <div className="h-1.5 rounded-full bg-[#f1f1f4] overflow-hidden">
                    <div
                      className="h-full rounded-full"
                      style={{ width: `${m.pct}%`, background: m.cat?.color ?? ACCENT }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}
      </div>

      <div className="shrink-0 border-t border-[#e5e7eb] p-3">
        <button
          onClick={onEditBrief}
          className="w-full inline-flex items-center justify-center gap-1.5 h-9 rounded-lg text-[12px] font-semibold border hover:bg-[rgba(124,92,255,0.06)] transition-colors"
          style={{ color: ACCENT, borderColor: "#c9bcff" }}
        >
          <Icon d={icons.edit} size={14} /> Edit brief
        </button>
        <p className="text-[10px] text-[#9ca3af] text-center mt-1.5">Saves as a new revision.</p>
      </div>
    </div>
  );
}

function Seg({
  value,
  onChange,
  options,
}: {
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
}) {
  return (
    <div className="inline-flex items-center rounded-md border border-[#e5e7eb] bg-[#f9fafb] p-0.5">
      {options.map((o) => (
        <button
          key={o.value}
          onClick={() => onChange(o.value)}
          className={
            "px-2.5 h-7 rounded-[5px] text-[12px] font-medium transition-colors " +
            (value === o.value ? "bg-white text-[#1f2937] shadow-sm" : "text-[#9ca3af]")
          }
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}
